import { useState } from 'react';
import { NudosButton } from '../NudosComponents';
import NudosConfirmActionModal from '../NudosComponents/NudosConfirmActionModal/NudosConfirmActionModal';
import { updateLogisticServiceReportStatus } from '../../services/services';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { REPORT_STATUS } from '../../types/orders';
import { ILogisticReports, IUpdateLogisticServiceReportStatusBody } from '../../types/services';
import './OpenLogisticServiceReportPanel.scss';

interface ILogisticServiceOpenReportRowProps {
  report: ILogisticReports;
  logisticServiceId: number;
  onStatusUpdated: () => void;
}

interface IOpenLogisticServiceReportPanelProps {
  openReports: ILogisticReports[];
  logisticServiceId: number;
  onStatusUpdated: () => void;
}

const LogisticServiceOpenReportRow = ({ report, logisticServiceId, onStatusUpdated }: ILogisticServiceOpenReportRowProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const closeReport = async () => {
    const reportId = report.reportId;
    if (reportId == null) return;
    setIsSubmitting(true);

    const body: IUpdateLogisticServiceReportStatusBody = {
      reportId,
      status: REPORT_STATUS.CLOSED,
      logisticServiceId
    };

    try {
      await updateLogisticServiceReportStatus(body);

      displaySuccessNotification({
        customJSXMessage: <>Reporte cerrado con éxito.</>
      });

      setShowCloseModal(false);
      onStatusUpdated();
    } catch {
      displayErrorNotification();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="openReportRow">
      <div className="openReportRowHeading">
        <span className="openReportRowReason">{report.reasonNameString || 'Reporte sin título'}</span>
      </div>
      <div className="openReportRowControls">
        <NudosButton
          buttonText="Cerrar reporte"
          handleClick={() => setShowCloseModal(true)}
          customClassName="openReportSubmitButton"
          isButtonDisabled={isSubmitting}
          isButtonLoading={isSubmitting}
        />
      </div>
      {showCloseModal && (
        <NudosConfirmActionModal
          modalText={
            <>
              Estás seguro que deseas cerrar el reporte? El cliente ya no visualizará el banner en la interna del servicio logístico
            </>
          }
          firstButtonText="Cancelar"
          secondButtonText="Confirmar"
          firstButtonAction={() => setShowCloseModal(false)}
          secondButtonAction={closeReport}
          isLoading={isSubmitting}
        />
      )}
    </div>
  );
}

const OpenLogisticServiceReportPanel = ({ openReports, logisticServiceId, onStatusUpdated }: IOpenLogisticServiceReportPanelProps) => {
  return (
    <div className="openLogisticServiceReportPanel">
      <div className="openReportPanelTitle">Reporte activo</div>
      <p className="openReportPanelDescription">
        El cliente puede estar viendo un aviso.
        Cuando el caso esté atendido, cierra este reporte para dejar de mostrarlo.
      </p>
      <div className="openReportRows">
        {openReports.map((report, index) => (
          <LogisticServiceOpenReportRow
            key={report.reportId ?? `${report.reasonNameStringId ?? 'report'}-${index}`}
            report={report}
            logisticServiceId={logisticServiceId}
            onStatusUpdated={onStatusUpdated}
          />
        ))}
      </div>
    </div>
  );
};

export default OpenLogisticServiceReportPanel;
